/**
 * CreateCenterModal - 새 센터를 생성하는 모달 컴포넌트
 *
 * - 센터 이름(필수), 위치(선택), 연락처(선택) 입력 폼 제공
 * - useCreateCenter 훅을 사용하여 Cloud Function 호출
 * - 생성 성공 시 onCreated 콜백 호출
 */

import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import { useCreateCenter } from "@/hooks/useCenters";
import type { CreateCenterParams } from "@/hooks/useCenters";

interface CreateCenterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateCenterModal({ isOpen, onClose, onCreated }: CreateCenterModalProps) {
  const [centerName, setCenterName] = useState("");
  const [location, setLocation] = useState("");
  const [contact, setContact] = useState("");

  const createCenter = useCreateCenter();

  // 모달이 열릴 때 폼 초기화
  useEffect(() => {
    if (isOpen) {
      setCenterName("");
      setLocation("");
      setContact("");
      createCenter.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const params: CreateCenterParams = {
      centerName: centerName.trim(),
    };

    if (location.trim()) {
      params.location = location.trim();
    }

    if (contact.trim()) {
      params.contact = contact.trim();
    }

    createCenter.mutate(params, {
      onSuccess: () => {
        onCreated();
      },
    });
  };

  const isSubmitDisabled = !centerName.trim() || createCenter.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">새 센터 추가</h2>

        <form onSubmit={handleSubmit}>
          {/* 센터 이름 (필수) */}
          <div className="mb-4">
            <label htmlFor="centerName" className="block text-sm font-medium text-gray-700 mb-1">
              센터 이름 <span className="text-red-500">*</span>
            </label>
            <input
              id="centerName"
              type="text"
              value={centerName}
              onChange={(e) => setCenterName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* 위치 (선택) */}
          <div className="mb-4">
            <label
              htmlFor="centerLocation"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              위치
            </label>
            <input
              id="centerLocation"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="예: 서울시 강남구"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* 연락처 (선택) */}
          <div className="mb-4">
            <label htmlFor="centerContact" className="block text-sm font-medium text-gray-700 mb-1">
              연락처
            </label>
            <input
              id="centerContact"
              type="text"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="예: 02-1234-5678"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* 에러 메시지 */}
          {createCenter.isError && (
            <p className="text-sm text-red-600 mb-4">
              센터 생성에 실패했습니다. 다시 시도해주세요.
            </p>
          )}

          {/* 버튼 영역 */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createCenter.isPending ? "생성 중..." : "추가"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
